// import { getField, updateField } from "vuex-map-fields";
import BaseStore from './baseStore'
export const namespaced = true
import { getNewId } from '@/utils/id'
import { lineString, lineIntersect } from '@turf/turf'
import generateBaseImportFromStage from '@/store/baseImportFromStage'
import { generateMultipleItemOverview } from '@/store/baseOverview'
import api from '../api/api'

const baseUrl = '/soil_profiles/'

const baseStore = new BaseStore(baseUrl)
const baseOverview = generateMultipleItemOverview({
  itemFilter: (soilProfile, stabSchematisationStage) =>
    soilProfile.soil_composition === stabSchematisationStage.soilcomposition,
  duplicationCheckFunc: (soilProfile, soilCompositionId) =>
    soilProfile.soil_composition === soilCompositionId
})
const baseImportFromStage = generateBaseImportFromStage({})

const getDefaultState = () => {
  return {
    ...structuredClone(baseStore.state),
    soilProfileSections: {
      x_start: null,
      x_end: null,
      x_start_method: 'distance',
      x_end_method: 'distance',
      x_start_height_available: true,
      x_end_height_available: true
    }
  }
}

export const state = {
  ...getDefaultState(),
  ...structuredClone(baseOverview.state),
  ...structuredClone(baseImportFromStage.state)
}

export const mutations = {
  ...baseStore.mutations,
  ...baseOverview.mutations,
  ...baseImportFromStage.mutations,
  RESET_STATE(state) {
    Object.assign(state, getDefaultState())
  },
  RESET_SOIL_PROFILE_SECTIONS(state) {
    state.soilProfileSections = getDefaultState().soilProfileSections
  },
  SET_SOIL_PROFILE_SECTION(state, payload) {
    state.soilProfileSections = payload
  }
}

export const actions = {
  ...baseStore.actions,
  ...baseOverview.actions,
  ...baseImportFromStage.actions,
  resetState({ commit }) {
    commit('RESET_STATE')
  },
  resetSoilProfileSections({ commit }) {
    commit('RESET_SOIL_PROFILE_SECTIONS')
  },
  async importSoilProfilesFromStage(
    { commit, dispatch },
    { sourceSchematisationStageId, targetSchematisationStageId, soilCompositionId }
  ) {
    try {
      commit('TOGGLE_IMPORT_FROM_STAGE_LOADING')
      const { imported_soil_profiles, imported_soil_profile_points } = await dispatch(
        'importFromStage',
        {
          import_option: {
            source_stab_schematisation_stage_id: sourceSchematisationStageId,
            target_stab_schematisation_stage_id: targetSchematisationStageId
          },
          soil_composition: soilCompositionId
        }
      )
      commit('SET_ITEMS', imported_soil_profiles)
      commit('soilProfilePoint/SET_ITEMS', imported_soil_profile_points, {
        root: true
      })
    } catch (e) {
      console.log(e)
    } finally {
      commit('TOGGLE_IMPORT_FROM_STAGE_LOADING')
    }
  },
  async createSoilProfile(context, payload) {
    context.commit('TOGGLE_WRITE_LOADING')
    let ids = getNewId(context.state.items, 'id')
    let compositionId = context.rootGetters['stabSchematisationStage/currentCompositionId']
    payload['soil_composition'] = compositionId
    payload['id'] = ids
    try {
      let response = await api.post(baseUrl, payload)
      context.commit('ADD_ITEM', response.data)
    } catch (e) {
      console.log(e)
    }
    context.commit('TOGGLE_WRITE_LOADING')
  },
  async calculateSection(context, payload) {
    context.commit('TOGGLE_WRITE_LOADING')
    try {
      let compositionId = context.rootGetters['stabSchematisationStage/currentCompositionId']
      const query = new URLSearchParams(payload).toString()
      const url = `${baseUrl}${compositionId}/get_x_start_x_end/?${query}`
      let response = await api.get(url, payload)
      context.commit('SET_SOIL_PROFILE_SECTION', response.data)
    } catch (e) {
      console.log(e)
    }
    context.commit('TOGGLE_WRITE_LOADING')
  }
}

export const getters = {
  ...baseOverview.getters,
  getPlotData:
    (state, getters, rootState, rootGetters) =>
    (stabSchematisationStage = null) => {
      let plot = []
      const items = getters.getItemsByStabSchematisationStage(stabSchematisationStage)
      const plotDataMaxY = rootGetters['topoProfilePoint/getPlotDataMaxY'](stabSchematisationStage)
      if (items && items.length && isFinite(plotDataMaxY)) {
        items.forEach((item) => {
          if (item.profile_type === 'wedge') return
          plot.push(
            getters.getSingleXLineSoilLayer({
              soilProfile: item,
              stabSchematisationStage
            })
          )
        })
      }
      return plot
    },
  getSingleXLineSoilLayer:
    (state, getters) =>
    ({ soilProfile, stabSchematisationStage = null }) => {
      let styleLine = {
        color: 'black',
        dash: 'dot',
        width: 1
      }
      let currentItem = state.currentItem
      if (currentItem && soilProfile.id === currentItem.id && !stabSchematisationStage) {
        styleLine = {
          color: 'black',
          dash: 'dot',
          width: 4
        }
      }
      let id = soilProfile.id
      let scatter = {
        x: [],
        y: [],
        line: { simplify: false },
        mode: 'lines',
        showlegend: false
      }
      const yCoordinate = getters.calculatePoint({
        x: soilProfile.x,
        stabSchematisationStage
      })[1]
      scatter.x = [soilProfile.x, soilProfile.x]
      scatter.y = [-50, yCoordinate]
      scatter.id = id
      scatter.line = styleLine
      return scatter
    },
  calculatePoint:
    (state, getters, rootState, rootGetters) =>
    ({ x, stabSchematisationStage = null }) => {
      const plotDataMaxY = rootGetters['topoProfilePoint/getPlotDataMaxY'](stabSchematisationStage)
      const intersectLine = [
        [x, -500],
        [x, plotDataMaxY]
      ]
      let topoProfileGeometry = lineString(
        rootGetters['topoProfilePoint/getTopoProfileGeometry'](stabSchematisationStage)
      )
      let intersects = lineIntersect(topoProfileGeometry, lineString(intersectLine))
      return intersects.features.length ? intersects.features[0].geometry.coordinates : [x, 50]
    },
  getSortedSoilProfile: (state) => {
    return [...state.items].sort(function (a, b) {
      return a.x - b.x
    })
  }
}
